/**
 * React Query hooks for the Team Leader Exam Management Workspace
 * (`api/teamleader/exams/*`). Query hooks accept the same `{pageNumber, pageSize, search,
 * sortBy, sortDir}` shape `usePaginationState()` produces, plus the domain-specific filter
 * fields — so a future Dashboard/Detail page can spread `usePaginationState()`'s return value
 * directly into `TlExamListParams`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  allowTlRetake,
  reattemptKeepProgress,
  reattemptWithRewatch,
  resetContentAfterExam,
  bulkAllowTlRetake,
  bulkResetTlAttempts,
  fetchTlAllAttempts,
  fetchTlAnalyticsSummary,
  fetchTlAtRiskStudents,
  fetchTlAuditHistory,
  fetchTlExamDetail,
  fetchTlExamInsights,
  fetchTlExamQuestionInsights,
  fetchTlExamsDashboard,
  fetchTlExamSummary,
  fetchTlStudentExamProfile,
  fetchTlStudentTimeline,
  forceSubmitTlExam,
  resetTlAttempt,
  sendTlBulkMessage,
  type BulkActionResult,
  type TlAtRiskParams,
  type TlAuditHistoryParams,
  type TlBulkMessageRequest,
  type TlBulkRetakeTarget,
  type TlExamListParams,
  type TlStudentTimelineParams,
} from "@/lib/api/tl-exams";

/**
 * Every TL-exam query key lives here. A same-key/different-`queryFn` collision is a known past bug
 * class in this codebase (two hooks sharing `["tl-exams", …]` with different fetchers made the last
 * writer win), so no route file should assemble one of these inline.
 */
export const keys = {
  dashboard: (params: TlExamListParams) => ["tl-exams", "dashboard", params] as const,
  dashboardRoot: ["tl-exams", "dashboard"] as const,
  allAttempts: (params: TlExamListParams) => ["tl-exams", "all-attempts", params] as const,
  allAttemptsRoot: ["tl-exams", "all-attempts"] as const,
  analyticsSummary: (params: TlExamListParams) =>
    ["tl-exams", "analytics-summary", params] as const,
  analyticsSummaryRoot: ["tl-exams", "analytics-summary"] as const,
  detail: (examId: string, params: TlExamListParams) =>
    ["tl-exams", "detail", examId, params] as const,
  detailForExam: (examId: string) => ["tl-exams", "detail", examId] as const,
  studentProfile: (studentId: string) => ["tl-exams", "student-profile", studentId] as const,
  auditHistory: (params: TlAuditHistoryParams) => ["tl-exams", "audit-history", params] as const,
  auditHistoryRoot: ["tl-exams", "audit-history"] as const,
  studentTimeline: (studentId: string, params: TlStudentTimelineParams) =>
    ["tl-exams", "student-timeline", studentId, params] as const,
  studentTimelineForStudent: (studentId: string) =>
    ["tl-exams", "student-timeline", studentId] as const,
  atRisk: (params: TlAtRiskParams) => ["tl-exams", "at-risk", params] as const,
  atRiskRoot: ["tl-exams", "at-risk"] as const,
  examInsights: (examId: string) => ["tl-exams", "exam-insights", examId] as const,
  examQuestionInsights: (examId: string) => ["tl-exams", "exam-question-insights", examId] as const,
  examSummary: (examId: string) => ["tl-exams", "summary", examId] as const,
  /**
   * Deliberately NOT under the `["tl-exams"]` prefix: it is a sibling of the admin Exam Center's
   * `["grading-queue"]`, so a grade saved on either surface invalidates both and the two never share
   * a cache entry with different fetchers.
   */
  gradingQueue: (examId: string) => ["grading-queue", examId] as const,
  /**
   * The caller's queue across EVERY exam — what /team-leader/exams/review reads. Sits under the same
   * `["grading-queue"]` prefix as the per-exam keys so one `gradingQueueRoot` invalidation still
   * refreshes both surfaces; "all" cannot collide with a sibling because those are GUIDs.
   */
  gradingQueueAll: ["grading-queue", "all"] as const,
  gradingQueueRoot: ["grading-queue"] as const,
};

// ── Queries ──────────────────────────────────────────────────────────────────

export function useTlExamsDashboard(params: TlExamListParams) {
  return useQuery({
    queryKey: keys.dashboard(params),
    queryFn: () => fetchTlExamsDashboard(params),
  });
}

export function useTlAllAttempts(params: TlExamListParams = {}) {
  return useQuery({
    queryKey: keys.allAttempts(params),
    queryFn: () => fetchTlAllAttempts(params),
  });
}

/**
 * The exam workspace header/Overview payload. One row, one request — this replaced a lookup that
 * fetched the caller's whole exam list purely to read one title, and it is the only source for the
 * exam's scheduling window and its course/track ids.
 */
export function useTlExamSummary(examId: string | null | undefined) {
  return useQuery({
    queryKey: keys.examSummary(examId ?? "none"),
    queryFn: () => fetchTlExamSummary(examId as string),
    enabled: !!examId,
  });
}

export function useTlAnalyticsSummary(params: TlExamListParams) {
  return useQuery({
    queryKey: keys.analyticsSummary(params),
    queryFn: () => fetchTlAnalyticsSummary(params),
  });
}

export function useTlExamDetail(examId: string | null | undefined, params: TlExamListParams) {
  return useQuery({
    queryKey: keys.detail(examId ?? "none", params),
    queryFn: () => fetchTlExamDetail(examId as string, params),
    enabled: !!examId,
  });
}

export function useTlStudentExamProfile(studentId: string | null | undefined) {
  return useQuery({
    queryKey: keys.studentProfile(studentId ?? "none"),
    queryFn: () => fetchTlStudentExamProfile(studentId as string),
    enabled: !!studentId,
  });
}

export function useTlAuditHistory(params: TlAuditHistoryParams = {}) {
  return useQuery({
    queryKey: keys.auditHistory(params),
    queryFn: () => fetchTlAuditHistory(params),
  });
}

export function useTlStudentTimeline(
  studentId: string | null | undefined,
  params: TlStudentTimelineParams = {},
) {
  return useQuery({
    queryKey: keys.studentTimeline(studentId ?? "none", params),
    queryFn: () => fetchTlStudentTimeline(studentId as string, params),
    enabled: !!studentId,
  });
}

export function useTlAtRiskStudents(params: TlAtRiskParams = {}) {
  return useQuery({
    queryKey: keys.atRisk(params),
    queryFn: () => fetchTlAtRiskStudents(params),
  });
}

export function useTlExamInsights(examId: string | null | undefined) {
  return useQuery({
    queryKey: keys.examInsights(examId ?? "none"),
    queryFn: () => fetchTlExamInsights(examId as string),
    enabled: !!examId,
  });
}

export function useTlExamQuestionInsights(examId: string | null | undefined) {
  return useQuery({
    queryKey: keys.examQuestionInsights(examId ?? "none"),
    queryFn: () => fetchTlExamQuestionInsights(examId as string),
    enabled: !!examId,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────
import { invalidateAllStudentState, TL_QUERY_KEYS } from "@/lib/api/query-options";
import type { QueryClient } from "@tanstack/react-query";

/**
 * Which caches a given write actually changes.
 *
 * Every flag below was derived by reading the service that performs the write, not assumed. The
 * previous behaviour was a blanket `invalidateAllTLState` + `invalidateAllStudentState` on every
 * mutation, which invalidated the whole `["tl-exams"]` and `["tl"]` prefixes — so one moderation
 * click refetched the roster, the insights, the question analytics, the summary, the student
 * profile, the timeline, the at-risk list, the audit history, the exam list *and* the student-search
 * combobox. In a tabbed workspace that is most of the application.
 */
interface TlExamRefreshScope {
  /** The per-exam roster (`detail`). */
  detail?: boolean;
  /** The workspace header + Overview metric grid. */
  summary?: boolean;
  /** Per-exam supervision insights (durations, groups, violations). */
  insights?: boolean;
  /** Per-question analytics. */
  questions?: boolean;
  /** The caller's grader-scoped pending queue. */
  queue?: boolean;
  /** The exams list page and its analytics rollup. */
  dashboard?: boolean;
  /** This student's TL-side profile, timeline and record. */
  student?: boolean;
  /** What the STUDENT sees: available/active exams, attempts remaining, dashboards. */
  studentPortal?: boolean;
  /** The caller's own "my exam actions" log. */
  audit?: boolean;
}

/**
 * Invalidate exactly the named keys for one write. Never invalidates a bare prefix.
 *
 * Returns the promise for the two caches the user is *looking at* (roster + summary); everything
 * else is fired and left to settle in the background, so a confirm button is not held hostage by a
 * refetch of a tab nobody has open. Lazy-mounted tabs make this safe: an unmounted query is
 * inactive, so invalidating it costs nothing and it refetches on the next tab switch.
 */
function invalidateTlExamScope(
  qc: QueryClient,
  scope: TlExamRefreshScope,
  ids: { examId?: string; studentId?: string },
): Promise<unknown> {
  const { examId, studentId } = ids;
  const foreground: Promise<unknown>[] = [];

  if (scope.detail && examId)
    foreground.push(qc.invalidateQueries({ queryKey: keys.detailForExam(examId) }));
  if (scope.summary && examId)
    foreground.push(qc.invalidateQueries({ queryKey: keys.examSummary(examId) }));

  if (scope.insights && examId) qc.invalidateQueries({ queryKey: keys.examInsights(examId) });
  if (scope.questions && examId)
    qc.invalidateQueries({ queryKey: keys.examQuestionInsights(examId) });
  if (scope.queue) qc.invalidateQueries({ queryKey: keys.gradingQueueRoot });
  if (scope.dashboard) {
    qc.invalidateQueries({ queryKey: keys.dashboardRoot });
    qc.invalidateQueries({ queryKey: keys.allAttemptsRoot });
    qc.invalidateQueries({ queryKey: keys.analyticsSummaryRoot });
    qc.invalidateQueries({ queryKey: keys.atRiskRoot });
  }
  if (scope.audit) qc.invalidateQueries({ queryKey: keys.auditHistoryRoot });
  if (scope.student && studentId) {
    qc.invalidateQueries({ queryKey: keys.studentProfile(studentId) });
    qc.invalidateQueries({ queryKey: keys.studentTimelineForStudent(studentId) });
    qc.invalidateQueries({ queryKey: TL_QUERY_KEYS.studentProfile(studentId) });
  }
  // The student-facing caches are a different application surface; a leader is never looking at
  // them, so they are always background work.
  if (scope.studentPortal) void invalidateAllStudentState(qc, studentId);

  return Promise.all(foreground);
}

function useTlExamMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  opts: {
    success: string;
    /**
     * Per-call success copy, for an action whose variants end in materially different states. Falls
     * back to `success` when absent or when it returns nothing.
     *
     * Currently unused: the two reattempt operations are separate mutations with their own fixed
     * copy, which is clearer than one mutation branching on a flag.
     */
    successFor?: (args: TArgs) => string | undefined;
    scope: TlExamRefreshScope;
    examId?: (args: TArgs) => string | undefined;
    studentId?: (args: TArgs) => string | undefined;
  },
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (_data, args) => {
      toast.success(opts.successFor?.(args) || opts.success);
      await invalidateTlExamScope(qc, opts.scope, {
        examId: opts.examId?.(args),
        studentId: opts.studentId?.(args),
      });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Something went wrong"),
  });
}

/**
 * The verified refresh sets. Each entry names the service method it was read from.
 *
 * Two of these contradict the Phase 1 architecture's table, which is why they were re-derived from
 * source rather than transcribed:
 *
 * - **Reset** (`ExamService.ResetAttemptForTeamLeaderAsync`) sets `ExcludedFromAttemptCount` plus
 *   three audit columns and explicitly never touches Score/Passed/Responses. That flag is read in
 *   exactly one place — `ExamStartPolicy`, which decides whether the *student* may start again.
 *   `TlExamDetailStudentRowDto` carries no reset marker and `BuildListItem` never looks at it, so a
 *   reset changes nothing the workspace displays. It refreshes the student's own state and the
 *   audit trail, and nothing else. (If a reset indicator is ever added to the roster row, this
 *   entry must gain `detail`.)
 * - **Grade essay** does *not* invalidate the audit history. `UpdateResponseGradeAsync` writes an
 *   `ExamAuditLogEntry` only — unlike `ExamService.AuditAsync`, it makes no `_activityLog.LogAsync`
 *   call, and the TL audit history is the ActivityLog read actor-scoped. Phase 1 marked this
 *   *inferred*; it is now verified, and it was wrong.
 */
const REFRESH = {
  /** Only `ExcludedFromAttemptCount` + audit columns change. Nothing in the workspace reads them. */
  reset: { student: true, studentPortal: true, audit: true },
  /** `GrantRetakeAsync` inserts an ExamRetakeGrant. No session is created, so no roster row moves. */
  retake: { student: true, studentPortal: true, audit: true },
  /**
   * Both reattempt operations write the SAME set, which is why they share one entry shape rather than
   * being described separately.
   *
   * Re-derived from `ExamService.ApplyReattemptAsync` rather than transcribed. They supersede the
   * passing sitting(s) for progression, may insert an `ExamRetakeGrant`, and recompute course and
   * track progression. That moves: the roster row (the sitting now carries a supersession marker),
   * the student's own state and portal (their gate just closed and their attempt balance just
   * changed), and the audit trail. It does NOT move the grading queue — a sitting awaiting grading
   * blocks the operation outright, so the queue cannot contain anything it touched.
   *
   * `insights`/`questions` are deliberately absent: no score, no answer and no session status
   * changes, so no per-question or score-derived metric can move.
   */
  reattemptRewatch: {
    detail: true,
    summary: true,
    dashboard: true,
    student: true,
    studentPortal: true,
    audit: true,
  },
  /** Same set as `reattemptRewatch` — the difference between the two is module progress, which the
   *  exam workspace does not display. */
  reattemptKeepProgress: {
    detail: true,
    summary: true,
    dashboard: true,
    student: true,
    studentPortal: true,
    audit: true,
  },
  /**
   * `ResetContentAfterExamAsync` writes only `UserModuleProgress` rows (plus their audit entries) and
   * recomputes course/track progression. It supersedes nothing, grants nothing and does not touch the
   * sitting — so unlike the two reattempts it moves NO exam-workspace row.
   *
   * What it does move is the student's own view of their course, which is why `studentPortal` is here:
   * modules they had finished are now unwatched. `audit` for the trail. `detail`/`summary`/`dashboard`
   * are deliberately absent — nothing the roster, the summary cards or the dashboard displays is
   * derived from module progress.
   */
  resetContentAfter: { student: true, studentPortal: true, audit: true },
  /**
   * Runs `CompleteExamAsync`, which scores the responses and can leave the session PendingGrading —
   * so the queue and the per-question analytics both move, along with every session-derived metric.
   */
  forceSubmit: {
    detail: true,
    summary: true,
    insights: true,
    questions: true,
    queue: true,
    dashboard: true,
    student: true,
    studentPortal: true,
    audit: true,
  },
  /** Notifications only. The one thing it changes is the caller's own action log. */
  message: { audit: true },
} satisfies Record<string, TlExamRefreshScope>;

// `studentId` is threaded through on every per-student write. The invalidation wrapper has always
// accepted it, but no caller supplied one — so the student-scoped caches it exists to refresh
// (exam profile, timeline, TL student record) were never invalidated by a moderation action, and
// the Student Sheet or profile page kept showing the pre-action attempt until something else
// happened to evict it.
export const useResetTlAttempt = () =>
  useTlExamMutation(
    (args: { sessionId: string; reason?: string; examId?: string; studentId?: string }) =>
      resetTlAttempt(args.sessionId, args.reason),
    {
      success: "Attempt reset.",
      scope: REFRESH.reset,
      examId: (a) => a.examId,
      studentId: (a) => a.studentId,
    },
  );

export const useAllowTlRetake = () =>
  useTlExamMutation(
    (args: { examId: string; studentId: string; reason?: string }) =>
      allowTlRetake(args.examId, args.studentId, args.reason),
    {
      success: "Retake granted.",
      scope: REFRESH.retake,
      examId: (a) => a.examId,
      studentId: (a) => a.studentId,
    },
  );

/**
 * Operation 1. The success copy names the consequence the student will actually meet, because
 * "reattempt authorized" alone is what left staff unable to answer "so what happens now?".
 */
export const useReattemptWithRewatch = () =>
  useTlExamMutation(
    (args: { sessionId: string; reason?: string; examId?: string; studentId?: string }) =>
      reattemptWithRewatch(args.sessionId, args.reason),
    {
      success:
        "New attempt authorized. The content after this exam is reset and locked — the student has to work through it again once they pass.",
      scope: REFRESH.reattemptRewatch,
      examId: (a) => a.examId,
      studentId: (a) => a.studentId,
    },
  );

/**
 * The downstream reset on its own. The success copy leads with what did NOT happen, because every
 * neighbouring action in the same menu authorizes a sitting and staff will otherwise assume this one
 * did too — and then tell the student to go and retake an exam they already passed.
 */
export const useResetContentAfterExam = () =>
  useTlExamMutation(
    (args: { sessionId: string; reason?: string; examId?: string; studentId?: string }) =>
      resetContentAfterExam(args.sessionId, args.reason),
    {
      success:
        "Content after this exam reset. The student's result and attempts are unchanged — they just have to work through that content again.",
      scope: REFRESH.resetContentAfter,
      examId: (a) => a.examId,
      studentId: (a) => a.studentId,
    },
  );

/** Operation 2. */
export const useReattemptKeepProgress = () =>
  useTlExamMutation(
    (args: { sessionId: string; reason?: string; examId?: string; studentId?: string }) =>
      reattemptKeepProgress(args.sessionId, args.reason),
    {
      success:
        "New attempt authorized. The content after this exam is locked until they pass, and none of their progress was changed.",
      scope: REFRESH.reattemptKeepProgress,
      examId: (a) => a.examId,
      studentId: (a) => a.studentId,
    },
  );

export const useForceSubmitTlExam = () =>
  useTlExamMutation(
    (args: { sessionId: string; examId?: string; studentId?: string }) =>
      forceSubmitTlExam(args.sessionId),
    {
      success: "Exam force-submitted.",
      scope: REFRESH.forceSubmit,
      examId: (a) => a.examId,
      studentId: (a) => a.studentId,
    },
  );

// The bulk variants hit the same services per item, so they carry the same refresh sets. They take
// no single studentId — a bulk run can touch dozens of students, and the per-student caches are
// keyed individually, so naming each would mean one invalidation call per row.
export const useBulkResetTlAttempts = () =>
  useTlExamMutation(
    (args: { sessionIds: string[]; reason?: string; examId?: string }): Promise<BulkActionResult> =>
      bulkResetTlAttempts(args.sessionIds, args.reason),
    { success: "Bulk reset completed.", scope: REFRESH.reset, examId: (a) => a.examId },
  );

export const useBulkAllowTlRetake = () =>
  useTlExamMutation(
    (args: {
      targets: TlBulkRetakeTarget[];
      reason?: string;
      examId?: string;
    }): Promise<BulkActionResult> => bulkAllowTlRetake(args.targets, args.reason),
    { success: "Bulk retake granted.", scope: REFRESH.retake, examId: (a) => a.examId },
  );

/**
 * A bulk message writes notifications and an entry in the caller's own action log — nothing else.
 * It used to invalidate roughly twenty query prefixes, including the exam roster and every
 * student-portal cache, for a write that changes neither.
 */
export const useSendTlBulkMessage = () =>
  useTlExamMutation(
    (args: TlBulkMessageRequest): Promise<BulkActionResult> => sendTlBulkMessage(args),
    { success: "Message sent.", scope: REFRESH.message },
  );

export { invalidateTlExamScope, REFRESH as TL_EXAM_REFRESH };
